import React from 'react';
import { clustersAPI } from '../../services/api';

// ── Manifest section (reusable) ───────────────────────────────────────────────

function ManifestSection({ title, description, fetchFn, filename, allowNamespace = false }) {
  const [rawManifest, setRawManifest] = React.useState(null);
  const [namespace, setNamespace] = React.useState('sarvkube-agent');
  const [loading, setLoading] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const manifest = rawManifest && allowNamespace
    ? rawManifest.replace(/sarvkube-agent/g, namespace || 'sarvkube-agent')
    : rawManifest;

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetchFn();
      setRawManifest(r.data.manifest || r.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const copy = () => {
    if (!manifest) return;
    navigator.clipboard.writeText(manifest).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    const blob = new Blob([manifest], { type: 'text/yaml' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename;
    a.click();
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div>
          <strong style={{ fontSize: 14 }}>{title}</strong>
          <span style={{ color: 'var(--muted)', fontSize: 13, marginLeft: 8 }}>{description}</span>
        </div>
        {!rawManifest && (
          <button className="btn btn-secondary btn-sm" onClick={load} disabled={loading}>
            {loading ? 'Loading...' : 'Show YAML'}
          </button>
        )}
      </div>

      {rawManifest && allowNamespace && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8,
          background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px' }}>
          <span style={{ fontSize: 12, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>Namespace:</span>
          <input
            className="input"
            value={namespace}
            onChange={e => setNamespace(e.target.value)}
            style={{ fontFamily: 'monospace', flex: 1, maxWidth: 280 }}
            placeholder="sarvkube-agent"
          />
          <span style={{ fontSize: 11, color: 'var(--muted)' }}>
            Change for beta/prod to avoid conflicts
          </span>
        </div>
      )}

      {manifest && (
        <>
          <textarea readOnly value={manifest} rows={14}
            style={{
              width: '100%', padding: '8px 12px', border: '1px solid var(--border-strong)',
              borderRadius: 6, fontSize: 11, fontFamily: 'monospace',
              background: '#1a202c', color: '#e2e8f0', resize: 'vertical', boxSizing: 'border-box',
            }}
          />
          <div style={{ marginTop: 6, display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={copy}>
              {copied ? 'Copied!' : 'Copy YAML'}
            </button>
            <button className="btn btn-secondary btn-sm" onClick={download}>
              Download
            </button>
            <code style={{ fontSize: 11, color: 'var(--ink-2)', alignSelf: 'center' }}>
              kubectl apply -f {filename}
            </code>
          </div>
        </>
      )}
    </div>
  );
}

// ── Agent / DaemonSet setup — handles all cluster states ─────────────────────
//
// Scenarios:
//   A. agent mode + never/disconnected  → show install instructions prominently (Step 1 + Step 2)
//   B. direct/imported + private IP + agent not connected → show DaemonSet required banner + YAML
//   C. direct/imported + public IP + agent not connected → show DaemonSet auto-installing or manual YAML
//   D. agent connected (any type)       → show compact connected badge only
//
export function ClusterAgent({ clusterId, agentStatus, connectionType, networkType, ingressStatus, agentScope }) {
  const [installData, setInstallData] = React.useState(null);
  const [copiedCmd, setCopiedCmd] = React.useState(false);
  const [agentLive, setAgentLive] = React.useState(agentStatus);
  const [scope, setScope] = React.useState(agentScope || 'read');
  const [scopeSaving, setScopeSaving] = React.useState(false);
  const [scopeMsg, setScopeMsg] = React.useState('');

  React.useEffect(() => { setScope(agentScope || 'read'); }, [agentScope]);

  const changeScope = async (newScope) => {
    if (newScope === scope || scopeSaving) return;
    setScopeSaving(true); setScopeMsg('');
    try {
      await clustersAPI.updateAgentScope(clusterId, newScope);
      setScope(newScope);
      // Re-apply the agent manifest so the new scope's k8s RBAC (and resources) are
      // DURABLY in place — not just the app-level flag. Non-fatal if it can't run now.
      setScopeMsg(newScope === 'write'
        ? 'Enabling write — updating the agent…'
        : 'Setting read-only — updating the agent…');
      try {
        await clustersAPI.redeployAgent(clusterId);
        setScopeMsg(newScope === 'write'
          ? 'Write enabled ✓ — the agent can now create/edit/delete workloads.'
          : 'Read-only ✓ — the agent can only view resources.');
      } catch (re) {
        setScopeMsg('Scope saved, but agent update needs the master agent connected. '
          + 'Click "Update Agent" when it is, or it applies on next reinstall.');
      }
    } catch (e) {
      setScopeMsg(e.response?.data?.detail || 'Failed to update scope');
    } finally {
      setScopeSaving(false);
    }
  };

  const [redeploying, setRedeploying] = React.useState(false);
  const redeploy = async () => {
    setRedeploying(true); setScopeMsg('Updating agent…');
    try {
      await clustersAPI.redeployAgent(clusterId);
      setScopeMsg('Agent updated ✓ — re-applied with current permissions + resources.');
    } catch (e) {
      setScopeMsg(e.response?.data?.detail || 'Agent update failed (master agent must be connected).');
    } finally {
      setRedeploying(false);
    }
  };

  // Poll agent status every 10s until connected
  React.useEffect(() => {
    setAgentLive(agentStatus);
    if (agentStatus === 'connected') return;
    const interval = setInterval(async () => {
      try {
        const r = await clustersAPI.getAgentStatus(clusterId);
        if (r.data.agent_connected) {
          setAgentLive('connected');
          clearInterval(interval);
        }
      } catch { /* ignore */ }
    }, 10000);
    return () => clearInterval(interval);
  }, [clusterId, agentStatus]);

  // The install command exists only for agent-mode clusters (the API answers 400 "not in
  // agent mode" for kubeconfig ones), and only the agent-mode view below shows it.
  React.useEffect(() => {
    if (connectionType !== 'agent') return;
    clustersAPI.getAgentInstallCmd(clusterId)
      .then(r => setInstallData(r.data))
      .catch(() => {});
  }, [clusterId, connectionType]);

  const copyCmd = (cmd) => {
    navigator.clipboard.writeText(cmd).catch(() => {});
    setCopiedCmd(true);
    setTimeout(() => setCopiedCmd(false), 2000);
  };

  // ── D: Agent connected — show compact status bar ──────────────────────────
  if (agentLive === 'connected') {
    return (
      <div className="card" style={{ border: '1px solid var(--color-success-50)', background: 'var(--color-success-50)', padding: '12px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 18 }}>✓</span>
          <div>
            <strong style={{ color: 'var(--color-success-700)' }}>SarvKube Agent Connected</strong>
            <span style={{ color: 'var(--ink-2)', fontSize: 13, marginLeft: 12 }}>
              Live resources, per-node metrics and kubectl access are active.
            </span>
          </div>
          {ingressStatus && ingressStatus !== 'installed' && (
            <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--color-warning-700)',
              background: 'var(--color-warning-50)', border: '1px solid var(--color-warning-500)', borderRadius: 4, padding: '2px 8px' }}>
              {ingressStatus === 'pending_manual'
                ? 'Ingress: manual setup needed on master'
                : `Ingress: ${ingressStatus}`}
            </span>
          )}
        </div>
        {/* Show ingress instructions for private clusters even when agent connected */}
        {(ingressStatus === 'pending_manual' || (networkType === 'private' && ingressStatus !== 'installed')) && (
          <IngressInstructions />
        )}

        {/* Agent scope: read (view only) vs write (create/edit/delete workloads) */}
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--color-success-500)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-1)', marginBottom: 6 }}>Agent permissions</div>
          <div style={{ display: 'flex', gap: 10 }}>
            {[
              { value: 'read',  icon: '👁', label: 'Read Only',   desc: 'View resources, metrics, logs' },
              { value: 'write', icon: '✏', label: 'Read + Write', desc: 'Create / edit / delete workloads' },
            ].map(opt => {
              const selected = scope === opt.value;
              return (
                <button
                  key={opt.value}
                  onClick={() => changeScope(opt.value)}
                  disabled={scopeSaving}
                  style={{
                    flex: 1, textAlign: 'left', cursor: scopeSaving ? 'wait' : 'pointer',
                    padding: '8px 12px', borderRadius: 6,
                    border: `1px solid ${selected ? 'var(--accent)' : 'var(--color-brand-200)'}`,
                    background: selected ? 'var(--accent-soft)' : 'var(--surface)',
                  }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>
                    {opt.icon} {opt.label} {selected && <span style={{ color: 'var(--accent)' }}>✓</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--muted)' }}>{opt.desc}</div>
                </button>
              );
            })}
          </div>
          {scopeMsg && <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 6 }}>{scopeMsg}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
            <button
              onClick={redeploy}
              disabled={redeploying || scopeSaving}
              style={{ fontSize: 12, padding: '5px 12px', borderRadius: 5,
                border: '1px solid var(--color-brand-200)', background: 'var(--surface)',
                cursor: (redeploying || scopeSaving) ? 'wait' : 'pointer' }}>
              {redeploying ? 'Updating…' : '↻ Update Agent'}
            </button>
            <span style={{ fontSize: 11, color: 'var(--muted)' }}>
              Re-applies the agent with the current scoped role + resources (upgrades older agents).
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6 }}>
            Write grants the agent a scoped Kubernetes role (workload create/edit/delete) — not cluster-admin. Takes effect on the agent's next heartbeat.
          </div>
        </div>
      </div>
    );
  }

  // ── A: Pure agent mode (no kubeconfig) — show full setup ─────────────────
  if (connectionType === 'agent') {
    return (
      <div className="card" style={{ border: '1px solid var(--brand-soft)', background: 'var(--brand-soft)' }}>
        <h2 style={{ color: 'var(--brand)', marginBottom: 4 }}>
          {agentLive === 'disconnected' ? '⚠ Agent disconnected' : '🔌 Connect Cluster Agent'}
        </h2>
        <p style={{ color: 'var(--ink-2)', marginBottom: 16, fontSize: 14 }}>
          Run this command on the <strong>cluster master node</strong>. It installs a systemd service
          that auto-starts, restarts on crash, and survives reboots.
        </p>
        <InstallCommand installData={installData} copied={copiedCmd} onCopy={copyCmd} />
        <DaemonSetSection clusterId={clusterId} required={true} />
      </div>
    );
  }

  // ── B/C: Imported with kubeconfig — show DaemonSet status ────────────────
  const isPrivate = networkType === 'private' ||
    (!networkType && connectionType !== 'agent');

  return (
    <div className="card" style={{
      border: isPrivate ? '2px solid var(--color-danger-50)' : '1px solid var(--brand-soft)',
      background: isPrivate ? 'var(--color-danger-50)' : 'var(--brand-soft)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 20 }}>{isPrivate ? '⚠' : '📡'}</span>
        <div>
          <h2 style={{ margin: 0, color: isPrivate ? 'var(--color-danger-700)' : 'var(--brand)', fontSize: 16 }}>
            {isPrivate
              ? 'Agent Required for Live Resources'
              : 'SarvKube Agent — Installing'}
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--ink-2)', fontSize: 13 }}>
            {isPrivate
              ? 'This cluster has a private IP — the API server is not reachable directly. The DaemonSet was auto-installed via kubeconfig. Once pods are running, live resources will appear automatically.'
              : 'The DaemonSet was automatically applied to your cluster. Pods are starting up — live resources will appear shortly.'}
          </p>
        </div>
      </div>

      {/* Ingress setup for private clusters */}
      {isPrivate && <IngressInstructions />}

      {/* DaemonSet YAML — in case auto-install failed */}
      <DaemonSetSection clusterId={clusterId} required={isPrivate} collapsed={!isPrivate} />
    </div>
  );
}

// ── Install command block ─────────────────────────────────────────────────────
function InstallCommand({ installData, copied, onCopy }) {
  return (
    <div style={{ background: '#1a202c', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ color: '#68d391', fontWeight: 600, fontSize: 13 }}>
          Run on master node (installs as systemd service)
        </span>
        <button
          onClick={() => installData && onCopy(installData.install_cmd)}
          style={{ background: '#2d3748', color: copied ? '#68d391' : '#a0aec0',
            border: 'none', borderRadius: 4, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}>
          {copied ? '✓ Copied' : 'Copy'}
        </button>
      </div>
      <code style={{ color: '#faf089', fontSize: 12, wordBreak: 'break-all',
        whiteSpace: 'pre-wrap', display: 'block' }}>
        {installData ? installData.install_cmd : 'Loading...'}
      </code>
      <div style={{ color: '#718096', fontSize: 11, marginTop: 10 }}>
        ✓ Auto-starts on boot &nbsp;·&nbsp; ✓ Restarts on crash &nbsp;·&nbsp; ✓ Works for any cluster
      </div>
    </div>
  );
}

// ── DaemonSet YAML section ────────────────────────────────────────────────────
function DaemonSetSection({ clusterId, required, collapsed = false }) {
  const [expanded, setExpanded] = React.useState(!collapsed);

  return (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: expanded ? 12 : 0 }}>
        <strong style={{ fontSize: 14 }}>
          {required ? '📦 Node Agent DaemonSet' : '📦 Node Agent DaemonSet (auto-installed)'}
        </strong>
        {required && (
          <span style={{ fontSize: 11, background: 'var(--color-danger-700)', color: 'white',
            borderRadius: 4, padding: '1px 6px' }}>Required</span>
        )}
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => setExpanded(e => !e)}
          style={{ marginLeft: 'auto' }}>
          {expanded ? 'Hide YAML' : 'Show YAML'}
        </button>
      </div>
      {expanded && (
        <ManifestSection
          title="sarvkube-agent-daemonset.yaml"
          description="Apply manually if auto-install failed"
          fetchFn={() => clustersAPI.getDaemonsetManifest(clusterId)}
          filename="sarvkube-agent-daemonset.yaml"
          allowNamespace={true}
        />
      )}
    </div>
  );
}

// ── Ingress setup instructions for private IP clusters ────────────────────────
function IngressInstructions() {
  const [show, setShow] = React.useState(false);

  return (
    <div style={{ background: 'var(--color-warning-50)', border: '1px solid var(--color-warning-500)', borderRadius: 6,
      padding: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <strong style={{ fontSize: 13, color: 'var(--color-warning-700)' }}>
          Ingress Setup Required (run on master node)
        </strong>
        <button onClick={() => setShow(s => !s)}
          style={{ background: 'none', border: '1px solid var(--color-warning-500)', borderRadius: 4,
            padding: '2px 10px', fontSize: 12, cursor: 'pointer', color: 'var(--color-warning-700)' }}>
          {show ? 'Hide' : 'Show commands'}
        </button>
      </div>
      {show && (
        <pre style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--ink)',
          background: 'var(--color-warning-50)', borderRadius: 4, padding: 10, marginTop: 10,
          overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
{`# 1. Install ingress-nginx (baremetal)
kubectl apply -f https://raw.githubusercontent.com/kubernetes/ingress-nginx/controller-v1.8.1/deploy/static/provider/baremetal/deploy.yaml

# 2. Allow non-root nginx to bind ports 80/443
sudo sysctl -w net.ipv4.ip_unprivileged_port_start=0
echo "net.ipv4.ip_unprivileged_port_start=0" | sudo tee /etc/sysctl.d/99-unprivileged-ports.conf

# 3. Get master node name
kubectl get nodes

# 4. Patch nginx controller to use host network (replace <master-node-name>)
kubectl patch deployment ingress-nginx-controller -n ingress-nginx --type=strategic -p '{
  "spec":{"template":{"spec":{
    "hostNetwork":true,
    "dnsPolicy":"ClusterFirstWithHostNet",
    "terminationGracePeriodSeconds":0,
    "nodeName":"<master-node-name>"
  }}}}'`}
        </pre>
      )}
    </div>
  );
}
